import React, { useEffect } from "react";
import styled from "styled-components";
import { useTranslation, Trans } from "react-i18next";
import { HashLink } from "react-router-hash-link";
import {
  MobileAltIcon,
  RocketIcon,
  LockIcon,
  UsersIcon,
  CheckCircleIcon,
  ClockIcon,
  ToolsIcon,
  CodeIcon,
  DatabaseIcon,
  CloudIcon,
} from "./IconComponents";
import SEO from "./SEO";
import RelatedServices from "./RelatedServices";

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 2rem 0;
  background-color: var(--light-background);
`;

const HeroSection = styled.section`
  background: var(--primary-color);
  color: white;
  padding: 6rem 0 4rem;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  opacity: 0.9;
  max-width: 700px;
  margin: 0 auto 2rem;
  line-height: 1.6;
`;

const ContentSection = styled.section`
  padding: 4rem 0;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 4rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const Card = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: white;
  font-size: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CardDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
`;

const ProcessSection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ProcessCard = styled.div`
  text-align: center;
  padding: 2rem;
`;

const ProcessNumber = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 auto 1.5rem;
`;

const ProcessTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const ProcessDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const BenefitsSection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin: 3rem 0;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--card-background);
  border-radius: 8px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
`;

const BenefitIcon = styled.div`
  color: var(--primary-color);
  font-size: 1.5rem;
  margin-top: 0.2rem;
  flex-shrink: 0;
`;

const BenefitText = styled.div`
  h5 {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    color: var(--text-color);
  }

  p {
    color: var(--text-color);
    line-height: 1.5;
    font-size: 0.95rem;
  }
`;

const TechnologySection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const TechnologyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const TechCard = styled.div`
  text-align: center;
  padding: 1.5rem;
  border-radius: 8px;
  background: var(--light-background);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const TechIcon = styled.div`
  color: var(--primary-color);
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const TechName = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
`;

const TechDescription = styled.p`
  color: var(--text-color);
  font-size: 0.9rem;
  line-height: 1.4;
`;

const CaseStudySection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const CaseStudyCard = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--primary-color),
      var(--secondary-color)
    );
  }
`;

const CaseStudyTitle = styled.h4`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CaseStudyText = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const CaseStudyResult = styled.div`
  background: var(--light-background);
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid var(--primary-color);

  h5 {
    color: var(--primary-color);
    margin-bottom: 0.5rem;
    font-size: 1rem;
  }

  p {
    color: var(--text-color);
    font-size: 0.9rem;
    margin: 0;
  }
`;

const ServiceTypesSection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const ServiceTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ServiceTypeCard = styled.div`
  background: var(--light-background);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  position: relative;
  border: 2px solid transparent;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    transform: translateY(-5px);
  }
`;

const ServiceTypeTitle = styled.h4`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const ServiceTypeDescription = styled.p`
  color: var(--text-color);
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const ServiceTypeFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    color: var(--text-color);
    padding: 0.5rem 0;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:before {
      content: "✓";
      color: var(--primary-color);
      font-weight: bold;
    }
  }
`;

const FAQSection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  background: var(--card-background);
  margin-bottom: 1rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FAQQuestion = styled.h4`
  padding: 1.5rem;
  margin: 0;
  background: var(--primary-color);
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: var(--secondary-color);
  }
`;

const FAQAnswer = styled.p`
  padding: 1.5rem;
  margin: 0;
  color: var(--text-color);
  line-height: 1.6;
`;

const CTASection = styled.section`
  background: var(--primary-color);
  color: white;
  padding: 4rem 0;
  text-align: center;
`;

// const CTATitle = styled.h3`
//   font-size: 2rem;
//   margin-bottom: 1rem;
// `;

// const CTADescription = styled.p`
//   font-size: 1.1rem;
//   margin-bottom: 2rem;
//   opacity: 0.9;
// `;

const CTAButton = styled(HashLink)`
  display: block;
  width: 50%;
  margin: 2rem auto 0;
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 4px;
  background-color: var(--primary-color);
  color: var(--light-text-color);
  font-size: 1.05rem;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background-color: var(--secondary-color);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid var(--primary-color);
    outline-offset: 4px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

// const CTAButton = styled.a`
//   display: inline-block;
//   background-color: white;
//   color: var(--primary-color);
//   padding: 1rem 2rem;
//   border-radius: 6px;
//   text-decoration: none;
//   font-weight: 600;
//   font-size: 1.1rem;
//   transition: all 0.3s ease;

//   &:hover {
//     transform: translateY(-2px);
//     box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
//     text-decoration: none;
//     color: var(--primary-color);
//   }
// `;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  color: var(--text-color);
`;

const SectionTitleQuestion = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 0rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const AppEntwicklung: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const advantages = [
    {
      icon: <RocketIcon />,
      title: "Native Performance",
      description:
        "Wir entwickeln Apps, die sich flüssig anfühlen und die Möglichkeiten von iOS und Android voll ausschöpfen – für ein Nutzererlebnis auf höchstem Niveau.",
    },
    {
      icon: <UsersIcon />,
      title: "Nutzerzentriertes Design",
      description:
        "Intuitive, ansprechende Bedienoberflächen, die Ihre Nutzer begeistern und die Interaktion mit Ihrer Marke zum Erlebnis machen.",
    },
    {
      icon: <CloudIcon />,
      title: "Nahtlose Backend-Anbindung",
      description:
        "Ihre App wird sicher und performant an bestehende Systeme, APIs und Cloud-Dienste angebunden – für konsistente Daten in Echtzeit.",
    },
    {
      icon: <LockIcon />,
      title: "Sicherheit von Anfang an",
      description:
        "Wir setzen auf bewährte Sicherheitsstandards bei Authentifizierung, Datenübertragung und -speicherung, damit sensible Daten Ihrer Nutzer geschützt bleiben.",
    },
  ];

  const process = [
    {
      title: "Konzeption & Strategie",
      description:
        "Gemeinsam definieren wir Zielgruppe, Kernfunktionen und die passende Plattformstrategie (nativ oder plattformübergreifend) für Ihre App.",
    },
    {
      title: "UI/UX-Design",
      description:
        "Entwicklung von Wireframes und Designs, die Nutzerfreundlichkeit mit einem modernen, markengerechten Erscheinungsbild verbinden.",
    },
    {
      title: "Entwicklung & Anbindung",
      description:
        "Programmierung der App mit modernen Frameworks sowie Integration von Backend-Diensten, APIs und Drittanbieter-Systemen.",
    },
    {
      title: "Testing, Launch & Betreuung",
      description:
        "Umfassende Tests auf verschiedenen Geräten, Veröffentlichung in den App Stores sowie laufende Wartung und Weiterentwicklung.",
    },
  ];

  const benefits = [
    {
      title: "Plattformübergreifende Reichweite",
      description:
        "Wir entwickeln für iOS und Android gleichermaßen, um die größtmögliche Zielgruppe mit einer konsistenten App-Erfahrung zu erreichen.",
    },
    {
      title: "Offline-Funktionalität",
      description:
        "Ihre App bleibt auch ohne durchgehende Internetverbindung nutzbar und synchronisiert Daten automatisch, sobald wieder eine Verbindung besteht.",
    },
    {
      title: "Push-Benachrichtigungen",
      description:
        "Aktive Nutzerbindung durch gezielte, personalisierte Benachrichtigungen direkt auf das Gerät Ihrer Kunden.",
    },
    {
      title: "Skalierbare Architektur",
      description:
        "Unsere App-Architekturen wachsen mit Ihrem Nutzeraufkommen mit, ohne dass Performance oder Stabilität leiden.",
    },
    {
      title: "App-Store-Optimierung",
      description:
        "Wir begleiten Sie bei der Veröffentlichung und Optimierung Ihrer App in Apple App Store und Google Play Store.",
    },
    {
      title: "Langfristiger Support",
      description:
        "Regelmäßige Updates, Kompatibilitätspflege bei neuen Betriebssystemversionen und kontinuierliche Weiterentwicklung Ihrer App.",
    },
  ];

  const technologies = [
    {
      icon: <MobileAltIcon />,
      name: "React Native",
      description:
        "Plattformübergreifende Apps mit nativer Performance aus einer Codebasis",
    },
    {
      icon: <CodeIcon />,
      name: "Swift & Kotlin",
      description:
        "Native Entwicklung für maximale Performance auf iOS und Android",
    },
    {
      icon: <ToolsIcon />,
      name: "Flutter",
      description:
        "Schnelle, plattformübergreifende Entwicklung mit einheitlichem Design",
    },
    {
      icon: <DatabaseIcon />,
      name: "Firebase & Datenbanken",
      description: "Zuverlässige Datenhaltung und Echtzeit-Synchronisation",
    },
    {
      icon: <CloudIcon />,
      name: "Cloud & APIs",
      description:
        "Skalierbare Backend-Anbindung und Integration von Drittanbieterdiensten",
    },
  ];

  const caseStudies = [
    {
      title: "Mobile App für den Außendienst",
      description:
        "Entwicklung einer plattformübergreifenden App zur Auftragsverwaltung und Dokumentation für Außendienstmitarbeiter eines mittelständischen Unternehmens.",
      result: {
        title: "Ergebnisse:",
        description:
          "40% schnellere Auftragsabwicklung, papierlose Dokumentation, Echtzeit-Synchronisation mit dem zentralen ERP-System",
      },
    },
    {
      title: "Kunden-App für den Einzelhandel",
      description:
        "Konzeption und Umsetzung einer Kunden-App mit Treueprogramm, Push-Benachrichtigungen und Bestellfunktion für eine Handelskette.",
      result: {
        title: "Ergebnisse:",
        description:
          "25% höhere Kundenbindung, deutlich gesteigerte Wiederkaufrate, positive Bewertungen in beiden App Stores",
      },
    },
  ];

  const serviceTypes = [
    {
      title: "Native App-Entwicklung",
      description:
        "Maßgeschneiderte Apps für iOS und Android, entwickelt mit den jeweiligen nativen Technologien für maximale Performance.",
      features: [
        "Optimale Performance je Plattform",
        "Voller Zugriff auf Gerätefunktionen",
        "Individuelles UI/UX-Design",
        "App-Store-Veröffentlichung",
        "Langfristiger Support",
      ],
    },
    {
      title: "Plattformübergreifende Apps",
      description:
        "Effiziente Entwicklung für iOS und Android aus einer gemeinsamen Codebasis, ideal für schnelle Time-to-Market.",
      features: [
        "Eine Codebasis für beide Plattformen",
        "Schnellere Entwicklungszeit",
        "Konsistentes Nutzererlebnis",
        "Kosteneffiziente Wartung",
        "Einfache Erweiterbarkeit",
      ],
    },
    {
      title: "App-Modernisierung & Weiterentwicklung",
      description:
        "Wir überarbeiten bestehende Apps hinsichtlich Performance, Design und Funktionsumfang oder erweitern sie um neue Features.",
      features: [
        "Technologie-Migration",
        "UI/UX-Modernisierung",
        "Performance-Optimierung",
        "Neue Funktionen & Integrationen",
        "Kompatibilitätspflege",
      ],
    },
  ];

  const faqs = [
    {
      question:
        "Native App oder plattformübergreifende Lösung – was ist besser?",
      answer:
        "Das hängt von Ihren Anforderungen ab: Native Apps bieten maximale Performance und vollen Gerätezugriff, plattformübergreifende Lösungen wie React Native oder Flutter ermöglichen eine schnellere und kosteneffizientere Entwicklung für beide Plattformen. Wir beraten Sie individuell zur passenden Strategie.",
    },
    {
      question: "Wie lange dauert die Entwicklung einer App?",
      answer:
        "Die Entwicklungsdauer hängt vom Funktionsumfang ab. Einfache Apps können innerhalb weniger Wochen umgesetzt werden, komplexere Lösungen mit umfangreicher Backend-Anbindung benötigen entsprechend mehr Zeit. Nach der Konzeptionsphase erhalten Sie einen konkreten Zeitplan.",
    },
    {
      question: "Übernehmen Sie auch die Veröffentlichung im App Store?",
      answer:
        "Ja, wir begleiten Sie durch den kompletten Veröffentlichungsprozess im Apple App Store und Google Play Store, inklusive aller notwendigen Vorbereitungen und Freigabeprozesse.",
    },
    {
      question: "Kann die App an unsere bestehenden Systeme angebunden werden?",
      answer:
        "Ja, wir integrieren Ihre App nahtlos in bestehende Systeme wie ERP-Lösungen, CRM-Systeme, Dynamics 365 oder individuelle Backend-Dienste über sichere Schnittstellen.",
    },
    {
      question: "Was passiert nach dem Launch der App?",
      answer:
        "Wir bieten langfristigen Support inklusive Updates, Kompatibilitätspflege bei neuen Betriebssystemversionen und Weiterentwicklung neuer Funktionen, damit Ihre App dauerhaft erfolgreich bleibt.",
    },
  ];

  const CTAContainer = styled.div`
    text-align: center;
    margin-top: 4rem;
    margin-bottom: 2rem;
    padding: 3rem;
    background-color: var(--card-background);
    border-radius: 8px;
  `;

  return (
    <PageContainer>
      <SEO
        title="App-Entwicklung für iOS & Android"
        description="Individuelle iOS- und Android-Apps von Byte Forge: mobile Geschäftsprozesse, erstklassiges Nutzererlebnis und nahtlose Integration in Ihre Systeme."
        path="/app-entwicklung"
        keywords="App-Entwicklung, iOS App entwickeln, Android App entwickeln, mobile App Entwicklung"
      />
      <Container>
        <SectionTitle>
          <Trans>
            Mobile Apps, die <span>Ihre Nutzer begeistern</span>
          </Trans>
        </SectionTitle>
        <SectionSubtitle>
          Wir entwickeln individuelle iOS- und Android-Apps, die Ihre
          Geschäftsprozesse mobil machen und Ihren Kunden ein erstklassiges
          Nutzererlebnis bieten.
        </SectionSubtitle>
      </Container>
      <ContentSection>
        <Container>
          <SectionTitleQuestion>
            Warum <span>App-Entwicklung</span>?
          </SectionTitleQuestion>
          <Grid>
            {advantages.map((advantage, index) => (
              <Card key={index}>
                <IconWrapper>{advantage.icon}</IconWrapper>
                <CardTitle>{advantage.title}</CardTitle>
                <CardDescription>{advantage.description}</CardDescription>
              </Card>
            ))}
          </Grid>
        </Container>
      </ContentSection>

      <ProcessSection>
        <Container>
          <SectionTitle>
            Unser <span>Entwicklungsprozess</span>
          </SectionTitle>
          <ProcessGrid>
            {process.map((step, index) => (
              <ProcessCard key={index}>
                <ProcessNumber>{index + 1}</ProcessNumber>
                <ProcessTitle>{step.title}</ProcessTitle>
                <ProcessDescription>{step.description}</ProcessDescription>
              </ProcessCard>
            ))}
          </ProcessGrid>
        </Container>
      </ProcessSection>

      <BenefitsSection>
        <Container>
          <SectionTitle>
            Ihre <span>Vorteile</span>
          </SectionTitle>
          <BenefitsList>
            {benefits.map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitIcon>
                  <CheckCircleIcon />
                </BenefitIcon>
                <BenefitText>
                  <h5>{benefit.title}</h5>
                  <p>{benefit.description}</p>
                </BenefitText>
              </BenefitItem>
            ))}
          </BenefitsList>
        </Container>
      </BenefitsSection>

      <TechnologySection>
        <Container>
          <SectionTitle>
            Unsere <span>Technologien</span>
          </SectionTitle>
          <TechnologyGrid>
            {technologies.map((tech, index) => (
              <TechCard key={index}>
                <TechIcon>{tech.icon}</TechIcon>
                <TechName>{tech.name}</TechName>
                <TechDescription>{tech.description}</TechDescription>
              </TechCard>
            ))}
          </TechnologyGrid>
        </Container>
      </TechnologySection>

      <CaseStudySection>
        <Container>
          <SectionTitle>
            Erfolgreiche <span>Projekte</span>
          </SectionTitle>
          <CaseStudyGrid>
            {caseStudies.map((caseStudy, index) => (
              <CaseStudyCard key={index}>
                <CaseStudyTitle>{caseStudy.title}</CaseStudyTitle>
                <CaseStudyText>{caseStudy.description}</CaseStudyText>
                <CaseStudyResult>
                  <h5>{caseStudy.result.title}</h5>
                  <p>{caseStudy.result.description}</p>
                </CaseStudyResult>
              </CaseStudyCard>
            ))}
          </CaseStudyGrid>
        </Container>
      </CaseStudySection>

      <ServiceTypesSection>
        <Container>
          <SectionTitle>
            Unsere <span>Leistungen</span>
          </SectionTitle>
          <ServiceTypeGrid>
            {serviceTypes.map((service, index) => (
              <ServiceTypeCard key={index}>
                <ServiceTypeTitle>{service.title}</ServiceTypeTitle>
                <ServiceTypeDescription>
                  {service.description}
                </ServiceTypeDescription>
                <ServiceTypeFeatures>
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex}>{feature}</li>
                  ))}
                </ServiceTypeFeatures>
              </ServiceTypeCard>
            ))}
          </ServiceTypeGrid>
        </Container>
      </ServiceTypesSection>

      <FAQSection>
        <Container>
          <SectionTitle>
            Häufig gestellte <span>Fragen</span>
          </SectionTitle>
          <FAQContainer>
            {faqs.map((faq, index) => (
              <FAQItem key={index}>
                <FAQQuestion>{faq.question}</FAQQuestion>
                <FAQAnswer>{faq.answer}</FAQAnswer>
              </FAQItem>
            ))}
          </FAQContainer>
        </Container>
      </FAQSection>
      <Container>
        <CTAContainer>
          <SectionTitleQuestion>
            <Trans>
              Bereit für Ihre <span>eigene App?</span>
            </Trans>
          </SectionTitleQuestion>
          <SectionSubtitle>
            Lassen Sie uns gemeinsam die passende App-Lösung für Ihr Unternehmen
            entwickeln.
            <CTAButton smooth to="/#contact">
              Kostenloses Erstgespräch
            </CTAButton>
          </SectionSubtitle>
        </CTAContainer>
      </Container>
      <RelatedServices currentPath="/app-entwicklung" />
    </PageContainer>
  );
};

export default AppEntwicklung;
